import { graphqlClient } from '@/lib/graphql-client';
import {
  buildGetBasicInfoCtcLevelsQuery,
  buildGetBasicInfoDepartmentsQuery,
  buildGetBasicInfoManagersQuery,
} from '../graphql/basic-info-lookups';
import { CtcLevelOption } from '../types/basic-info-lookups.types';
import { DepartmentLookupOption, ManagerOption } from '../types/employee.types';

/** Every department fits in one page, so the list is sorted client-side by `Title`, like the old
 * department dropdowns. */
const byTitleAsc = <T extends { Title: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.Title.localeCompare(b.Title));

export const getBasicInfoDepartments = async (): Promise<DepartmentLookupOption[]> => {
  const result = await graphqlClient.query<{ Departments: { items: DepartmentLookupOption[] } }>({
    query: buildGetBasicInfoDepartmentsQuery({}, 1, 300),
  });
  return byTitleAsc(result.Departments.items);
};

/** Levels of the active organization, as maintained in Org Setup > CTC Levels. Listed lowest CTC
 * first so the dropdown reads as a ladder. */
export const getBasicInfoCtcLevels = async (): Promise<CtcLevelOption[]> => {
  const result = await graphqlClient.query<{ CtcLevels: { items: CtcLevelOption[] } }>({
    query: buildGetBasicInfoCtcLevelsQuery({}, 1, 100),
  });
  return [...result.CtcLevels.items].sort((a, b) => (a.CTC ?? 0) - (b.CTC ?? 0));
};

/** The first matches of employees whose name or e-mail contains `keyword` (case-insensitive), in the
 * query's server-side order (newest first). */
export const searchReportingManagers = async (keyword: string): Promise<ManagerOption[]> => {
  const pattern = keyword.trim();
  const filter: Record<string, unknown> = {};
  if (pattern) {
    filter.$or = [
      { DisplayName: { $regex: pattern, $options: 'i' } },
      { Email: { $regex: pattern, $options: 'i' } },
    ];
  }
  const result = await graphqlClient.query<{ Employees: { items: ManagerOption[] } }>({
    query: buildGetBasicInfoManagersQuery(filter, 1, 15),
  });
  return result.Employees.items;
};
